import { CalendarCheck } from "lucide-react";

import EHRCard from "@/src/components/cards/ehr-cards/ehr-card";
import { cn } from "@/src/lib/shadcn-utils";
import { formatFromUnderscoreToHumanReadable } from "@/src/lib/string";
import { formatHumanReadableDate } from "@/src/lib/utils/date-and-phone-utils";
import { Appointment } from "@/src/models/appointment";

interface EHRBookedAppointmentsCardProps {
  bookedAppointments: Appointment[];
  // When set, the table scrolls vertically past this height (a Tailwind max-h-* class) with the
  // header pinned, instead of growing the card. Off by default to preserve existing callers' layout.
  maxHeightClassName?: string;
  // Card heading; defaults to "Booked Appointments". Callers showing a filtered subset (e.g. only
  // upcoming) can relabel it. Also drives the empty-state copy.
  title?: string;
}

export default function EHRBookedAppointmentsCard({
  bookedAppointments,
  maxHeightClassName,
  title = "Booked Appointments",
}: EHRBookedAppointmentsCardProps) {
  return (
    <EHRCard icon={<CalendarCheck />} title={title}>
      {bookedAppointments.length === 0 && (
        <p className="text-sm text-muted-foreground">No {title.toLowerCase()} available</p>
      )}

      {bookedAppointments.length > 0 && (
        <div className={cn("overflow-x-auto", maxHeightClassName && `${maxHeightClassName} overflow-y-auto`)}>
          <table className="w-full text-sm">
            <thead className={cn(maxHeightClassName && "sticky top-0 bg-card")}>
              <tr className="border-b">
                <th className="text-left font-medium py-2 w-1/5">Date</th>
                <th className="text-left font-medium py-2 w-1/5">Type</th>
                <th className="text-left font-medium py-2 w-1/6">Status</th>
                <th className="text-left font-medium py-2 w-1/5">Created</th>
                <th className="text-left font-medium py-2 w-1/3">Notes</th>
              </tr>
            </thead>
            <tbody>
              {bookedAppointments.map((appointment) => (
                <tr key={appointment.id} className="border-b border-border/50">
                  <td className="py-2 pr-4">
                    {appointment.appointmentDatetime ? formatHumanReadableDate(appointment.appointmentDatetime) : "TBD"}
                  </td>
                  <td className="py-2 pr-4">{formatFromUnderscoreToHumanReadable(appointment.type) || "N/A"}</td>
                  <td className="py-2 pr-4">
                    {formatFromUnderscoreToHumanReadable(appointment.status || "") || "N/A"}
                  </td>
                  <td className="py-2 pr-4">
                    {appointment.appointmentCreatedAt
                      ? formatHumanReadableDate(appointment.appointmentCreatedAt)
                      : "N/A"}
                  </td>
                  <td className="py-2 pr-4 break-words" title={appointment.ehrReason || ""}>
                    {appointment.ehrReason || "N/A"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </EHRCard>
  );
}
